import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin, Package, ScanBarcode, X } from "lucide-react";

import { apiFetch } from "@/lib/api";
import BarcodeScanner from "@/components/BarcodeScanner";

const CHAIN_BIN_DELAY_MS = 450;

export default function Lokacije() {
  const queryClient = useQueryClient();
  const [sku, setSku] = useState("");
  const [binLocation, setBinLocation] = useState("");
  const [scannerTarget, setScannerTarget] = useState<"sku" | "bin" | null>(null);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const afterSkuCameraChainBinRef = useRef(false);
  const binInputRef = useRef<HTMLInputElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const chainTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (chainTimeoutRef.current) clearTimeout(chainTimeoutRef.current);
    },
    []
  );

  function handleScannerScan(text: string) {
    const decodedText = text.trim();
    if (scannerTarget === "sku") {
      setSku(decodedText);
      setScannerTarget(null);
      if (afterSkuCameraChainBinRef.current) {
        afterSkuCameraChainBinRef.current = false;
        if (chainTimeoutRef.current) clearTimeout(chainTimeoutRef.current);
        chainTimeoutRef.current = setTimeout(() => {
          chainTimeoutRef.current = null;
          binInputRef.current?.focus();
          setScannerTarget("bin");
        }, CHAIN_BIN_DELAY_MS);
      }
    } else {
      setBinLocation(decodedText);
      setScannerTarget(null);
    }
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ id: number; name: string; sku: string; binLocation: string }>(
        "/api/admin/products/bin-by-sku",
        {
          method: "POST",
          body: JSON.stringify({ sku: sku.trim(), binLocation: binLocation.trim() }),
        }
      ),
    onSuccess: (data) => {
      setSuccessMessage(`Spremljeno: ${data.name} → ${data.binLocation}`);
      setSku("");
      setBinLocation("");
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      window.setTimeout(() => skuInputRef.current?.focus(), 50);
    },
    onError: () => {
      setSuccessMessage(null);
    },
  });

  function openSkuScanner() {
    setSuccessMessage(null);
    afterSkuCameraChainBinRef.current = true;
    setScannerTarget("sku");
  }

  function openBinScanner() {
    setSuccessMessage(null);
    afterSkuCameraChainBinRef.current = false;
    setScannerTarget("bin");
  }

  return (
    <div className="max-w-lg mx-auto space-y-6 pb-24">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Lokacije</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Skenirajte SKU, zatim lokaciju u skladištu. Nakon SKU skeniranja kamera se automatski otvara za
          lokaciju.
        </p>
      </div>

      {successMessage && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
          {successMessage}
        </div>
      )}

      {saveMutation.isError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {saveMutation.error instanceof Error ? saveMutation.error.message : "Došlo je do greške."}
        </div>
      )}

      <div className="space-y-5 rounded-2xl border border-border/60 bg-white p-5 shadow-sm">
        <div>
          <label htmlFor="lokacije-sku" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
            SKU
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <Package className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-neutral-400 pointer-events-none" />
              <input
                ref={skuInputRef}
                id="lokacije-sku"
                value={sku}
                onChange={(e) => {
                  setSku(e.target.value);
                  afterSkuCameraChainBinRef.current = false;
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    binInputRef.current?.focus();
                  }
                }}
                placeholder="Skenirajte ili upišite"
                autoComplete="off"
                className="w-full h-12 rounded-xl border border-border bg-white pl-10 pr-3 text-sm font-mono placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground outline-none transition"
              />
            </div>
            <button
              type="button"
              onClick={openSkuScanner}
              className="h-12 shrink-0 px-3 rounded-xl border-2 border-neutral-200 bg-neutral-50 hover:bg-neutral-100 flex items-center justify-center gap-2 text-xs font-semibold text-neutral-700 transition active:scale-[0.98]"
              title="Skeniraj barkod (SKU)"
            >
              <ScanBarcode className="size-5" />
              <span className="hidden sm:inline">Skeniraj</span>
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="lokacije-bin" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
            Lokacija (bin)
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-neutral-400 pointer-events-none" />
              <input
                ref={binInputRef}
                id="lokacije-bin"
                value={binLocation}
                onChange={(e) => setBinLocation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (sku.trim() && binLocation.trim()) saveMutation.mutate();
                  }
                }}
                placeholder="npr. A-12"
                autoComplete="off"
                className="w-full h-12 rounded-xl border border-border bg-white pl-10 pr-3 text-sm font-mono placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground outline-none transition"
              />
            </div>
            <button
              type="button"
              onClick={openBinScanner}
              className="h-12 shrink-0 px-3 rounded-xl border-2 border-neutral-200 bg-neutral-50 hover:bg-neutral-100 flex items-center justify-center gap-2 text-xs font-semibold text-neutral-700 transition active:scale-[0.98]"
              title="Skeniraj barkod (lokacija)"
            >
              <ScanBarcode className="size-5" />
              <span className="hidden sm:inline">Skeniraj</span>
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        disabled={saveMutation.isPending || !sku.trim() || !binLocation.trim()}
        onClick={() => saveMutation.mutate()}
        className="w-full h-12 rounded-xl bg-foreground text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed transition active:scale-[0.98] shadow-lg shadow-foreground/15"
      >
        {saveMutation.isPending ? (
          <>
            <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Spremanje...
          </>
        ) : (
          <>
            <ScanBarcode className="size-4" />
            Spremi lokaciju
          </>
        )}
      </button>

      <BarcodeScanner
        open={scannerTarget !== null}
        onScan={handleScannerScan}
        onClose={() => { afterSkuCameraChainBinRef.current = false; setScannerTarget(null); }}
        title={scannerTarget === "sku" ? "Skeniraj SKU" : "Skeniraj lokaciju"}
        hint="Usmjerite kameru prema barkodu"
        scannerId="lokacije-barcode-reader"
        error={scannerError}
      />
    </div>
  );
}
